import { useEffect, useState } from 'react';
import {
  RiAddLine,
  RiDeleteBin6Line,
  RiKey2Line,
  RiRefreshLine,
  RiUserSettingsLine,
  RiVipCrownLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { api } from '../state/api';
import { useStore } from '../state/store';
import { useAuthStore } from '../state/authStore';
import { ROLE_LABEL, type AppUser, type AuthUser } from '../state/domain';
import { cn, formatDate, initials } from '@/lib/utils';

/** 账号管理（平台管理员）：自助注册关闭后，账号在此开户。 */
export function UsersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useStore(state => state.toast);
  const me = useAuthStore(state => state.user);

  const [items, setItems] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    username: '',
    name: '',
    role: 'analyst' as AuthUser['role'],
    password: '',
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const { items: list } = await api.listUsers();
      setItems(list);
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    try {
      await api.createUser({
        email: form.email.trim(),
        name: form.name.trim(),
        username: form.username.trim() || undefined,
        role: form.role,
        password: form.password,
      });
      toast('success', '账号已创建');
      setForm({ email: '', username: '', name: '', role: 'analyst', password: '' });
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const resetPassword = async (user: AppUser) => {
    const next = window.prompt(`为「${user.name}」设置新的初始口令（至少 8 位，含字母与数字）：`);
    if (!next) return;
    try {
      await api.updateUser(user.id, { resetPassword: next });
      toast('success', '口令已重置，该用户需重新登录');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const changeRole = async (user: AppUser, role: AuthUser['role']) => {
    try {
      await api.updateUser(user.id, { role });
      toast('success', '角色已更新');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const remove = async (user: AppUser) => {
    if (!window.confirm(`确认删除账号「${user.name}」（${user.email}）？`)) return;
    try {
      await api.deleteUser(user.id);
      toast('success', '账号已删除');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="w-[min(720px,95vw)]">
        <DialogHeader>
          <DialogTitle>账号管理</DialogTitle>
          <DialogDescription>
            自助注册已关闭。新分析员账号在此开户，开户后可在案件的「团队成员」中邀请加入。
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-[var(--radius)] border border-border bg-muted/40 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-medium">
            <RiAddLine className="size-3.5 text-primary" />
            开户
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1.5">
              <Label>邮箱 *</Label>
              <Input
                value={form.email}
                placeholder="analyst@example.com"
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>姓名 *</Label>
              <Input value={form.name} placeholder="张三" onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>用户名（登录用，留空取邮箱前缀）</Label>
              <Input
                value={form.username}
                placeholder="analyst"
                onChange={e => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>平台角色</Label>
              <Select value={form.role} onValueChange={value => setForm({ ...form, role: value as AuthUser['role'] })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABEL) as AuthUser['role'][]).map(role => (
                    <SelectItem key={role} value={role}>
                      {ROLE_LABEL[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>初始口令 *（至少 8 位，含字母与数字）</Label>
              <Input
                type="text"
                value={form.password}
                placeholder="让用户首次登录后自行修改"
                onChange={e => setForm({ ...form, password: e.target.value })}
              />
            </div>
          </div>
          <Button
            className="mt-2"
            size="sm"
            disabled={!form.email.trim() || !form.name.trim() || form.password.length < 8}
            onClick={create}
          >
            <RiAddLine className="size-3.5" />
            创建账号
          </Button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="label-caps">账号（{items.length}）</span>
          <Button size="xs" variant="ghost" onClick={refresh} disabled={loading}>
            <RiRefreshLine className={cn('size-3', loading && 'animate-spin')} />
            刷新
          </Button>
        </div>

        <div className="mt-2 flex max-h-[280px] flex-col gap-1.5 overflow-y-auto">
          {items.map(user => (
            <div
              key={user.id}
              className="flex items-center gap-2.5 rounded-[var(--radius)] border border-border bg-card p-2.5"
            >
              <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-muted text-[11px] font-semibold">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="size-full object-cover" />
                ) : (
                  initials(user.name)
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[12px] font-medium">{user.name}</span>
                  {user.role === 'admin' && (
                    <Badge variant="default" className="gap-1">
                      <RiVipCrownLine className="size-3" />
                      管理员
                    </Badge>
                  )}
                  {user.id === me?.id && <Badge variant="outline">当前登录</Badge>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 font-mono text-[10px] text-muted-foreground">
                  <span>{user.email}</span>
                  {user.username && <span>@{user.username}</span>}
                  <span>参与 {user.caseCount} 个案件</span>
                  {user.lastLoginAt && <span>最近登录 {formatDate(user.lastLoginAt)}</span>}
                </div>
              </div>

              <Select value={user.role} onValueChange={value => changeRole(user, value as AuthUser['role'])}>
                <SelectTrigger className="h-7 w-[84px] shrink-0 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABEL) as AuthUser['role'][]).map(role => (
                    <SelectItem key={role} value={role}>
                      {ROLE_LABEL[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="icon-sm"
                variant="ghost"
                title="重置口令"
                className="shrink-0 text-muted-foreground"
                onClick={() => resetPassword(user)}
              >
                <RiKey2Line className="size-3.5" />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                title="删除账号"
                disabled={user.id === me?.id}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => remove(user)}
              >
                <RiDeleteBin6Line className="size-3.5" />
              </Button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
              暂无账号。
            </div>
          )}
        </div>

        <p className="mt-4 flex items-center gap-1.5 border-t border-dashed border-border pt-3 text-[10.5px] text-muted-foreground">
          <RiUserSettingsLine className="size-3" />
          所有开户、改角色、重置口令与删除操作均写入审计日志。
        </p>
      </DialogContent>
    </Dialog>
  );
}